import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Logo from "./Logo";
import { Button } from "./ui/button";
import { Menu, X } from "lucide-react";

const links = [
  { id: "services", label: "Services" },
  { id: "about", label: "About" },
  { id: "process", label: "Process" },
  { id: "faq", label: "FAQ", route: "/faq" },
  { id: "quote", label: "Quote" },
];

const Navigation = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const isLanding = location.pathname === "/";
  const solid = scrolled || !isLanding;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Scroll to hash on landing page after navigation
  useEffect(() => {
    if (location.pathname === "/" && location.hash) {
      const id = location.hash.replace("#", "");
      setTimeout(() => {
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 50);
    }
  }, [location]);

  const scrollTo = (id, route) => {
    setOpen(false);
    if (route) {
      navigate(route);
      return;
    }
    if (location.pathname !== "/") {
      navigate(`/#${id}`);
      return;
    }
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const goHome = () => {
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/");
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        solid
          ? "bg-[#F7F6F2]/85 backdrop-blur-xl border-b border-[#D2D7D0]"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-24 flex items-center justify-between">
        <button
          data-testid="nav-home-logo"
          onClick={goHome}
          className="flex items-center"
        >
          <Logo variant={scrolled ? "dark" : "dark"} />
        </button>

        <nav className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              onClick={() => scrollTo(l.id, l.route)}
              className="text-sm font-medium text-[#1A3626] hover:text-[#234B35] transition-colors relative after:absolute after:left-0 after:-bottom-1 after:h-px after:w-0 after:bg-[#F5C242] hover:after:w-full after:transition-all"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="hidden md:block">
          <Button
            data-testid="nav-cta-quote"
            onClick={() => scrollTo("quote")}
            className="rounded-full bg-[#1A3626] text-white hover:bg-[#234B35] h-11 px-6 text-sm tracking-wide"
          >
            Request a Quote
          </Button>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          className="md:hidden p-2 rounded-full hover:bg-[#E8EAE6]"
          onClick={() => setOpen((o) => !o)}
          aria-label="Toggle menu"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {open && (
        <div
          data-testid="nav-mobile-menu"
          className="md:hidden border-t border-[#D2D7D0] bg-[#F7F6F2]/95 backdrop-blur-xl"
        >
          <div className="px-6 py-6 flex flex-col gap-4">
            {links.map((l) => (
              <button
                key={l.id}
                data-testid={`nav-mobile-link-${l.id}`}
                onClick={() => scrollTo(l.id, l.route)}
                className="text-left text-base text-[#1A3626]"
              >
                {l.label}
              </button>
            ))}
            <Button
              data-testid="nav-mobile-cta-quote"
              onClick={() => scrollTo("quote")}
              className="rounded-full bg-[#1A3626] text-white hover:bg-[#234B35] h-12 mt-2"
            >
              Request a Quote
            </Button>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navigation;
